
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useGameData } from '@/hooks/use-game-data';
import GameBoard from './GameBoard';
import GameResults from './GameResults';
import LevelSelector from './LevelSelector';
import { GAME_LEVELS } from '@/data/gameData';

interface GameModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export interface GameResult {
  score: number;
  moves: number;
  timeInSeconds: number;
  isNewBest: boolean;
  unlockedFact?: string;
  levelId?: number;
  gridId?: number;
  hasNextGrid?: boolean;
}

const GameModal = ({ isOpen, onClose }: GameModalProps) => {
  const { t } = useTranslation();
  const { recordGameResult, unlockFact, refreshGameData } = useGameData();
  const [gameState, setGameState] = useState<'levelSelect' | 'playing' | 'completed'>('levelSelect');
  const [gameResult, setGameResult] = useState<GameResult | null>(null);
  const [currentLevel, setCurrentLevel] = useState<number>(1);
  const [currentGrid, setCurrentGrid] = useState<number>(1);

  const handleLevelSelect = (levelId: number, gridId: number) => {
    console.log('🎮 GameModal - Level selected:', { levelId, gridId });
    setCurrentLevel(levelId);
    setCurrentGrid(gridId);
    setGameState('playing');
  };

  const getNextGrid = (levelId: number, currentGridId: number) => {
    const level = GAME_LEVELS.find(l => l.id === levelId);
    if (!level) return null;
    
    const currentGridIndex = level.grids.findIndex(g => g.id === currentGridId);
    const nextGridIndex = currentGridIndex + 1;
    
    if (nextGridIndex < level.grids.length) {
      return level.grids[nextGridIndex];
    }
    return null;
  };

  const handleGameComplete = async (moves: number, timeInSeconds: number) => {
    console.log('🎮 Game Modal - Game completed, processing result...');
    
    // Calculate score based on level difficulty, moves and time
    const level = GAME_LEVELS.find(l => l.id === currentLevel);
    const baseScore = 1000 + (currentLevel * 100);
    const movesPenalty = moves * 10;
    const timePenalty = Math.floor(timeInSeconds / 10) * 5;
    const timeBonus = level ? Math.max(0, (level.timeLimit - timeInSeconds) * 2) : 0;
    const finalScore = Math.max(100, baseScore - movesPenalty - timePenalty + timeBonus);

    const { isNewBest } = await recordGameResult(finalScore, moves, timeInSeconds, currentLevel, currentGrid);
    
    // Unlock a random cultural fact
    const facts = ['lac_assal', 'skoudehkaris', 'lahoh', 'lac_abbe', 'moucha'];
    const randomFact = facts[Math.floor(Math.random() * facts.length)];
    await unlockFact(randomFact);

    // Check if there's a next grid
    const nextGrid = getNextGrid(currentLevel, currentGrid);
    const hasNextGrid = nextGrid !== null;

    console.log('🎮 GameModal - Next grid check:', { 
      currentLevel, 
      currentGrid, 
      nextGrid: nextGrid?.id, 
      hasNextGrid 
    });

    setGameResult({
      score: finalScore,
      moves,
      timeInSeconds,
      isNewBest,
      unlockedFact: randomFact,
      levelId: currentLevel,
      gridId: currentGrid,
      hasNextGrid
    });
    setGameState('completed');
  };

  const handleNextGrid = () => {
    const nextGrid = getNextGrid(currentLevel, currentGrid);
    if (nextGrid) {
      console.log('🎮 GameModal - Moving to next grid:', nextGrid.id);
      setCurrentGrid(nextGrid.id);
      setGameResult(null);
      setGameState('playing');
    }
  };

  const handlePlayAgain = () => {
    setGameState('levelSelect');
    setGameResult(null);
  };

  const handleClose = async () => {
    console.log('🎮 GameModal - Closing modal and refreshing data');
    
    // Always refresh data when closing to ensure UI is up to date
    await refreshGameData();
    
    // Reset state and close
    setGameState('levelSelect');
    setGameResult(null);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setGameState('levelSelect');
      setGameResult(null);
    }
  }, [isOpen]);

  const getCurrentLevelAndGrid = () => {
    const level = GAME_LEVELS.find(l => l.id === currentLevel);
    const grid = level?.grids.find(g => g.id === currentGrid);
    return { level, grid };
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md mx-auto max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-center bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            {t('game.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {gameState === 'levelSelect' ? (
            <LevelSelector 
              onLevelSelect={handleLevelSelect}
              onClose={handleClose}
            />
          ) : gameState === 'playing' ? (
            <GameBoard 
              onGameComplete={handleGameComplete}
              levelData={getCurrentLevelAndGrid()}
            />
          ) : (
            <GameResults 
              result={gameResult!} 
              onPlayAgain={handlePlayAgain}
              onNextGrid={handleNextGrid}
              onClose={handleClose}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GameModal;
